import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
} from "react";
import { useLocation } from "react-router-dom";
import {
  useForm,
  UseFormSetValue,
  UseFormGetValues,
  Controller,
  useFieldArray,
  useWatch,
  FieldArrayWithId,
  Control,
  FieldErrors,
} from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import {
  Box,
  Paper,
  Typography,
  TextField,
  TextFieldProps,
  Button,
  Stack,
  Alert,
  CircularProgress,
  FormControl,
  FormControlLabel,
  List,
  ListItem,
  ListItemText,
  Autocomplete,
  Table,
  TableBody,
  TableCell,
  TableRow,
  TableHead,
  TableContainer,
  Collapse,
  Chip,
  Switch,
  Divider,
  IconButton,
  Tooltip,
  InputAdornment,
  Tab,
  Tabs,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Popover,
} from "@mui/material";
import {
  ArrowBack as ArrowBackIcon,
  Add as AddIcon,
  Save as SaveIcon,
  Edit as EditIcon,
  Lock as LockIcon,
  Visibility as ViewIcon,
  Cancel as CancelIcon,
} from "@mui/icons-material";
import { ShippingIcon } from "../../components/common/NucleusSymbolsAndIcons";
import { DatePicker } from "@mui/x-date-pickers";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import {
  useTSQShipment,
  useTSMCreateShipment,
  useTSMUpdateShipment,
} from "../../hooks/useTSShipments";
import { useTSQItemList } from "../../hooks/useTSItems";
import { useTSQUOMUnitList } from "../../hooks/useTSUnits";
import { useTSQCompanyList, useTSQPeopleList } from "../../hooks/useTSEntities";
import { useTSQAddressList, useTSQAddress } from "../../hooks/useTSAddresses";
import {
  ShipmentRecordFull,
  ShipmentRecordCreate,
  ShipmentRecordUpdate,
  ShipmentPackageInfoFull,
  ShipmentPackageInfoCreate,
  ShipmentPackageInfoUpdate,
  ShippingDestination,
  DefaultShipDestination,
  SHIPMENT_REASONS,
  ShipmentReason,
  SHIPPING_CARRIERS,
  ShippingCarrier,
  SHIPMENT_STATUSES,
  ShipmentStatus,
  SHIPSTATION_RESIDENTIAL_INDICATORS,
  ShipStationResidentialInidicator,
  PackageOption,
  PackageOptions,
} from "../../types/shipmentsTypes";
import {
  PACKAGE_WEIGHT_UNITS,
  PackageWeightUnit,
  TableObject,
} from "../../types/common/baseTableTypes";
import {
  OrderAddressEdit,
  OrderAddressEditKey,
  OrderAddressEditDefaults,
} from "../../types/addressTypes";
import {
  InvMovBatchCreate,
  InvMovCreate,
  InvMovBatchFull,
  InvMovFull,
} from "../../types/invMovementTypes";
import { ShippingMethods } from "../../types/common/termTypes";
import { TypedSelectOption } from "../../types/common/selectOptions";
import { AddressFieldOld } from "../../components/common/AddressViewEdit";
import {
  NumericDisplayField,
  NumericEntryField,
} from "../../components/common/CustomFields";
import stringUtils from "../../utils/stringUtils";
import { useWorldLocationData } from "../../contexts/WorldLocationContextProvider";
import statusUtils from "../../utils/statusUtils";

type AddressOption = TypedSelectOption<"address", { entity_id: string }>;
const CustomAddressOption: AddressOption = {
  label: "Custom",
  group: "Custom",
  type: "address",
  value: {
    id: "custom",
    entity_id: "custom",
  },
};
const CenteseAddressOption: AddressOption = {
  label: "Centese",
  group: "Centese",
  type: "address",
  value: {
    id: "centese",
    entity_id: "centese",
  },
};

type EntityOption = TypedSelectOption<"entity", { entity_type: string }>;
const CenteseEntityOption: EntityOption = {
  label: "Centese",
  group: "Centese",
  type: "entity",
  value: {
    id: "centese",
    entity_type: "centese",
  },
};
const CustomEntityOption: EntityOption = {
  label: "Custom",
  group: "Custom",
  type: "entity",
  value: {
    id: "custom",
    entity_type: "custom",
  },
};

type ItemOption = TypedSelectOption<
  "item",
  {
    number: string;
    name: string;
    uomTypeId: string;
    preferredUom: string | null;
    item_type: string;
    expires: boolean;
  }
>;
const CustomItemOption: ItemOption = {
  label: "Custom Item",
  type: "item",
  value: {
    id: "custom",
    number: "Custom Item",
    name: "Custom Item",
    uomTypeId: "custom",
    preferredUom: "custom",
    item_type: "custom",
    expires: false,
  },
};
type UOMOption = TypedSelectOption<
  "unit",
  { symbol: string; conversion: number; typeId: string }
>;
const CustomUOMOption: UOMOption = {
  label: "Custom Unit",
  type: "unit",
  value: {
    id: "custom",
    symbol: "custom",
    conversion: 1,
    typeId: "custom",
  },
};

type LotOption = TypedSelectOption<
  "lot",
  {
    itemId: string;
    relRev: string | null;
    expDate: string | null;
  }
>;

type PosOption = TypedSelectOption<
  "position",
  {
    lotId: string;
    lotNum: string;
    itemId: string;
    itemNum: string;
    locId: string;
    locName: string;
    statId: string;
    statName: string;
    quant: number;
  }
>;

type PackageWeightUnitOption = TypedSelectOption<"pkg_weight_unit">;
const PackageWeightUnitOptions: PackageWeightUnitOption[] =
  PACKAGE_WEIGHT_UNITS.map((pwu) => ({
    label: stringUtils.capFirstLetter(pwu),
    type: "pkg_weight_unit",
    value: {
      id: pwu as PackageWeightUnit,
    },
  }));

type ShipmentReasonOption = TypedSelectOption<"ship_reason">;
const ShipmentReasonOptions: ShipmentReasonOption[] = SHIPMENT_REASONS.map(
  (r) => ({
    label: stringUtils.capFirstLetter(r.replace(/_/g, " ")),
    type: "ship_reason",
    value: {
      id: r as ShipmentReason,
    },
  }),
);
type ShippingCarrierOption = TypedSelectOption<"ship_carrier">;
const ShippingCarrierOptions: ShippingCarrierOption[] = SHIPPING_CARRIERS.map(
  (c) => ({
    label: stringUtils.capFirstLetter(c.replace(/_/g, " ")),
    type: "ship_carrier",
    value: {
      id: c as ShippingCarrier,
    },
  }),
);
type ShippingMethodOption = TypedSelectOption<"ship_method">;
const ShippingMethodOptions: {
  FEDEX: ShippingMethodOption[];
  UPS: ShippingMethodOption[];
  LTL: ShippingMethodOption[];
  DHL: ShippingMethodOption[];
  OTHER: ShippingMethodOption[];
  ANY: ShippingMethodOption[];
  NONE: ShippingMethodOption[];
} = (() => {
  const retVal = {
    FEDEX: ShippingMethods["fedex"]["methods"].map(
      (m) =>
        ({
          label: m,
          type: "ship_method",
          value: {
            id: m.toUpperCase(),
          },
        }) as ShippingMethodOption,
    ),
    UPS: ShippingMethods["ups"]["methods"].map(
      (m) =>
        ({
          label: m,
          type: "ship_method",
          value: {
            id: m.toUpperCase(),
          },
        }) as ShippingMethodOption,
    ),
    LTL: [
      {
        label: "Any",
        type: "ship_method",
        value: {
          id: "Any".toUpperCase(),
        },
      } as ShippingMethodOption,
    ],
    DHL: [
      {
        label: "Any",
        type: "ship_method",
        value: {
          id: "Any".toUpperCase(),
        },
      } as ShippingMethodOption,
    ],
    OTHER: [
      {
        label: "Any",
        type: "ship_method",
        value: {
          id: "Any".toUpperCase(),
        },
      } as ShippingMethodOption,
    ],
    ANY: [
      {
        label: "Any",
        type: "ship_method",
        value: {
          id: "Any".toUpperCase(),
        },
      } as ShippingMethodOption,
    ],
    NONE: [
      {
        label: "None",
        type: "ship_method",
        value: {
          id: "None".toUpperCase(),
        },
      } as ShippingMethodOption,
    ],
  };
  return retVal;
})();
type ShippingMethodOptionsKey = [keyof typeof ShippingMethodOptions];

type ShipmentStatusOption = TypedSelectOption<"shipment_status">;
const ShipmentStatusOptions: ShipmentStatusOption[] = SHIPMENT_STATUSES.map(
  (s) => ({
    label: stringUtils.capFirstLetter(s.replace(/_/g, " ")),
    type: "shipment_status",
    value: {
      id: s as ShipmentStatus,
    },
  }),
);

type RecordTypeOption = TypedSelectOption<"rec_type">;

type AssociatedRecordOption = TypedSelectOption<
  "assoc_record",
  { recType: string }
>;

// type PackageOption = TypedSelectOption<
//   "package",
//   {
//     catalog: string | null;
//     weight: PackageWeight | null;
//     dimensions: PackageDimensions | null;
//   }
// >;
// const PackageOptions: PackageOption[] = [
//   {
//     label: "Custom Package",
//     type: "package",
//     value: {
//       id: "custom",
//       catalog: null,
//       weight: null,
//       dimensions: null,
//     },
//   },
//   {
//     label: "TGDK",
//     type: "package",
//     value: {
//       id: "tgdk",
//       catalog: "TGDK110012",
//       weight: {
//         value: "7.5",
//         unit: "pound",
//       },
//       dimensions: {
//         height: "12",
//         length: "16",
//         width: "16",
//       },
//     },
//   },
//   {
//     label: "TGDK Box",
//     type: "package",
//     value: {
//       id: "tgdkbox",
//       catalog: null,
//       weight: null,
//       dimensions: {
//         height: "12",
//         length: "16",
//         width: "16",
//       },
//     },
//   },
//   {
//     label: "TGCT20",
//     type: "package",
//     value: {
//       id: "tgct20",
//       catalog: "TGCT120020",
//       weight: {
//         value: "4",
//         unit: "pound",
//       },
//       dimensions: {
//         height: "8",
//         length: "12",
//         width: "12",
//       },
//     },
//   },
//   {
//     label: "TGCT28",
//     type: "package",
//     value: {
//       id: "tgct28",
//       catalog: "TGCT120028",
//       weight: {
//         value: "4.5",
//         unit: "pound",
//       },
//       dimensions: {
//         height: "8",
//         length: "12",
//         width: "12",
//       },
//     },
//   },
//   {
//     label: "TGCT Box",
//     type: "package",
//     value: {
//       id: "tgctbox",
//       catalog: null,
//       weight: null,
//       dimensions: {
//         height: "8",
//         length: "12",
//         width: "12",
//       },
//     },
//   },
//   {
//     label: "TGCM",
//     type: "package",
//     value: {
//       id: "tgcm",
//       catalog: "TGCM1000",
//       weight: {
//         value: "7.5",
//         unit: "pound",
//       },
//       dimensions: {
//         height: "9",
//         length: "17",
//         width: "14",
//       },
//     },
//   },
//   {
//     label: "TGCM Box",
//     type: "package",
//     value: {
//       id: "tgcmbox",
//       catalog: null,
//       weight: null,
//       dimensions: {
//         height: "9",
//         length: "17",
//         width: "14",
//       },
//     },
//   },
//   {
//     label: "TGPS",
//     type: "package",
//     value: {
//       id: "tgps",
//       catalog: "TGPS0100",
//       weight: {
//         value: "1.5",
//         unit: "pound",
//       },
//       dimensions: {
//         height: "2",
//         length: "9",
//         width: "6",
//       },
//     },
//   },
//   {
//     label: "White Mailer Box",
//     type: "package",
//     value: {
//       id: "whitemailer",
//       catalog: null,
//       weight: null,
//       dimensions: {
//         height: "2",
//         length: "9",
//         width: "6",
//       },
//     },
//   },
// ];

type ResidentialIndicatorOption = TypedSelectOption<"residential_indicator">;
const ResidentialIndicatorOptions: ResidentialIndicatorOption[] = [
  {
    label: "Unknown",
    type: "residential_indicator",
    value: {
      id: "unknown" as ShipStationResidentialInidicator,
    },
  },
  {
    label: "Yes",
    type: "residential_indicator",
    value: {
      id: "yes" as ShipStationResidentialInidicator,
    },
  },
  {
    label: "No",
    type: "residential_indicator",
    value: {
      id: "no" as ShipStationResidentialInidicator,
    },
  },
];

interface PackageFormData {
  status: ShipmentStatus;
  nuc_pkg_id: string;
  dimensions: TableObject<"pkg_dims">;
  weight: TableObject<"pkg_weight">;
  tracking: string | null;
  package_number: number;
  package_contents: string;
  is_new: boolean;
  movements: InvMovCreate[];
  description: string | null;
  created_at: string | null;
  updated_at: string | null;
}

interface DialogPackageData {
  duplicates: number;
  status: ShipmentStatusOption | null;
  package: PackageOption | null;
  dimensions: TableObject<"pkg_dims">;
  weight: {
    value: string;
    unit: PackageWeightUnitOption | null;
  };
  tracking: string | null;
  package_contents: string | null;
  is_new: boolean;
  movements: InvMovCreate[];
  description: string | null;
}
const dialogPkgDefaults = {
  duplicates: 1,
  status: ShipmentStatusOptions[0],
  package: PackageOptions[0],
  dimensions: {
    height: 0.0,
    length: 0.0,
    width: 0.0,
  },
  weight: {
    value: "0",
    unit: PackageWeightUnitOptions[0],
  },
  tracking: null,
  package_contents: null,
  is_new: true,
  movements: [],
  description: null,
};

interface ShipmentFormData {
  name: string;
  status: ShipmentStatusOption | null;
  reason: ShipmentReasonOption | null;
  reason_other: string | null;
  carrier: ShippingCarrierOption | null;
  carrier_other: string | null;
  method: ShippingMethodOption | null;
  associated_record_type: RecordTypeOption | null;
  associated_record: AssociatedRecordOption | null;
  ship_to: ShippingDestination;
  ship_from: ShippingDestination;
  packages: PackageFormData[];
  primary_tracking: string | null;
  shipment_date: string | null;
  description: string | null;
  created_at: string | null;
  updated_at: string | null;
}

const shipYupSchema = yup.object({
  name: yup.string().required(),
  status: yup.object().required(),
  reason: yup.object().required(),
  carrier: yup.object().required(),
  method: yup.object().required(),
  ship_to: yup
    .object({
      nuc_entity_id: yup.string().required(),
      nuc_address_id: yup.string().required(),
      name: yup.string().required(),
      phone: yup.string().required(),
      address_line1: yup.string(),
      city_locality: yup.string(),
      state_province: yup.string(),
      postal_code: yup.string(),
      country_code: yup.string().length(2),
      address_residential_indicator: yup
        .string()
        .oneOf(["unknown", "yes", "no"]),
      email: yup.string().nullable(),
      company_name: yup.string().nullable(),
      address_line2: yup.string().nullable(),
      address_line3: yup.string().nullable(),
      instructions: yup.string().nullable(),
    })
    .required(),
  ship_from: yup
    .object({
      nuc_entity_id: yup.string().required(),
      nuc_address_id: yup.string().required(),
      name: yup.string().required(),
      phone: yup.string().required(),
      address_line1: yup.string(),
      city_locality: yup.string(),
      state_province: yup.string(),
      postal_code: yup.string(),
      country_code: yup.string().length(2),
      address_residential_indicator: yup
        .string()
        .oneOf(["unknown", "yes", "no"]),
      email: yup.string().nullable(),
      company_name: yup.string().nullable(),
      address_line2: yup.string().nullable(),
      address_line3: yup.string().nullable(),
      instructions: yup.string().nullable(),
    })
    .required(),
  packages: yup.array(yup.object()).min(1),
  primary_tracking: yup.string().nullable(),
  shipment_date: yup.string().nullable(),
  description: yup.string().nullable(),
  created_at: yup.string().nullable(),
  updated_at: yup.string().nullable(),
});

const formDefaults = {
  name: "",
  status: ShipmentStatusOptions[0],
  reason: ShipmentReasonOptions[0],
  reason_other: null,
  carrier: ShippingCarrierOptions[0],
  carrier_other: null,
  method: ShippingMethodOptions["FEDEX"][0],
  associated_record_type: null,
  associated_record: null,
  ship_to: DefaultShipDestination,
  ship_from: DefaultShipDestination,
  packages: [],
  primary_tracking: null,
  shipment_date: null,
  description: null,
  created_at: null,
  updated_at: null,
};

interface ShipAddressEditProps<
  TValue extends Record<string, any> = Record<string, any>,
> {
  formMode: string;
  selectLabel: string;
  fieldName: "ship_to" | "ship_from";
  currOpt: AddressOption | null;
  updateCurrOpt: React.Dispatch<React.SetStateAction<AddressOption | null>>;
  availOpts: AddressOption[];
  setValue: UseFormSetValue<ShipmentFormData>;
  currValues: ShippingDestination;
  errors: any;
  textFieldProps?: Omit<
    TextFieldProps,
    "value" | "onChange" | "type" | "slotProps.input"
  >;
}

const ShipAddressEditField: React.FC<ShipAddressEditProps> = <
  TValue extends Record<string, any> = Record<string, any>,
>({
  formMode,
  selectLabel,
  fieldName,
  currOpt,
  updateCurrOpt,
  availOpts,
  setValue,
  currValues,
  errors,
  textFieldProps = {},
}: ShipAddressEditProps<TValue>) => {
  const [open, setOpen] = useState<boolean>(false);
  const [editMode, setEditMode] = useState<boolean>(false);
  const anchorElRef = useRef<any>(null);
  const [resIndicator, setResIndicator] =
    useState<ResidentialIndicatorOption | null>(null);

  const isCustomOpt = useMemo(() => {
    return currOpt?.value.id === "custom";
  }, [currOpt]);

  const {
    data: baseAddrData,
    isLoading: baseAddrLoading,
    error: baseAddrError,
  } = useTSQAddress(currOpt?.value.id || "", !!currOpt && !isCustomOpt);

  const handleClose = () => {
    if (editMode) {
      handleAddrSave();
    } else {
      setOpen(false);
    }
  };

  const handleEditButtonPress = (event: any) => {
    if (editMode) {
      handleAddrSave();
    } else {
      setEditMode(true);
    }
  };

  const handleAddrSave = () => {
    if (formMode === "create" || formMode === "edit") {
      setEditMode(false);
    }
    setOpen(false);
  };

  const handleAddrChange = (event: any, value: AddressOption | null) => {
    updateCurrOpt(value);
    let newVal = DefaultShipDestination;
    newVal["nuc_entity_id"] = currValues["nuc_entity_id"];
    setValue(`${fieldName}`, newVal);
  };

  useEffect(() => {
    if (baseAddrData) {
      setValue(`${fieldName}.nuc_address_id`, baseAddrData.id);
      setValue(`${fieldName}.name`, baseAddrData.attn || "");
      setValue(`${fieldName}.company_name`, baseAddrData.addressee || "");
      setValue(`${fieldName}.phone`, baseAddrData.phone || "999-999-9999");
      setValue(`${fieldName}.address_line1`, baseAddrData.line_1);
      setValue(`${fieldName}.address_line2`, baseAddrData.line_2 || null);
      setValue(`${fieldName}.address_line3`, baseAddrData.line_3 || null);
      setValue(`${fieldName}.city_locality`, baseAddrData.city);
      setValue(`${fieldName}.state_province`, baseAddrData.state);
      setValue(`${fieldName}.postal_code`, baseAddrData.zip);
      setValue(`${fieldName}.country_code`, baseAddrData.country);
      setValue(`${fieldName}.address_residential_indicator`, "unknown");
      setValue(`${fieldName}.email`, baseAddrData.email || null);
    }
  }, [baseAddrData, setValue, fieldName]);
  return (
    <Stack sx={{ direction: "column", gap: 1, width: "100%" }}>
      <Box sx={{ display: "flex", gap: 2, width: "100%" }}>
        <Autocomplete
          value={currOpt}
          fullWidth
          ref={anchorElRef}
          onChange={handleAddrChange}
          options={availOpts}
          disabled={!["create", "edit"].includes(formMode)}
          getOptionLabel={(option) => option?.label || ""}
          isOptionEqualToValue={(option, value) =>
            option.value.id === value?.value.id
          }
          renderInput={(params) => {
            const endAdorn: React.ReactNode = (
              <Box
                sx={{
                  display: "flex",
                  gap: 1,
                }}
              >
                <InputAdornment position="end">
                  <Tooltip title="Edit Address">
                    <IconButton
                      onClick={() => setOpen((prev) => !prev)}
                      edge="end"
                      size="small"
                      disabled={formMode === "view" || formMode === "receive"}
                    >
                      <EditIcon />
                    </IconButton>
                  </Tooltip>
                </InputAdornment>
                {params.InputProps.endAdornment}
              </Box>
            );
            params.InputProps.endAdornment = endAdorn;
            return (
              <TextField
                {...params}
                {...textFieldProps}
                label={selectLabel}
                error={!!errors}
                helperText={
                  errors
                    ? "Address Error. Edit Address to see errors."
                    : undefined
                }
              />
            );
          }}
        />
        <Popover
          open={open}
          anchorEl={anchorElRef.current}
          onClose={handleClose}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "center",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "center",
          }}
          slotProps={{
            paper: {
              sx: {
                width: anchorElRef.current
                  ? `${anchorElRef.current.offsetWidth * 1.5}px`
                  : "auto",
              },
            },
          }}
        >
          {isCustomOpt || baseAddrData ? (
            <Stack
              sx={{
                direction: "column",
                gap: 1,
                p: 1,
                width: "100%",
              }}
            >
              {(formMode === "create" || formMode === "edit") && (
                <Button
                  variant="outlined"
                  color={editMode ? "primary" : "info"}
                  onClick={handleEditButtonPress}
                >
                  {editMode ? "Save Edits" : "Edit Address"}
                </Button>
              )}
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "repeat(4,1fr)",
                  gridAutoRows: "auto",
                }}
              >
                <Typography
                  variant="subtitle1"
                  sx={{ gridColumn: 1, gridRow: 1 }}
                >
                  Name:
                </Typography>
                <Box
                  sx={{
                    gridColumn: 2,
                    gridRow: 1,
                    display: "flex",
                    width: "100%",
                    justifyContent: "space-between",
                  }}
                >
                  <Typography variant="subtitle1">{currValues.name}</Typography>
                </Box>
                <Typography
                  variant="subtitle1"
                  sx={{ gridColumn: 1, gridRow: 2 }}
                >
                  Company:
                </Typography>
                <Box
                  sx={{
                    gridColumn: 2,
                    gridRow: 2,
                    display: "flex",
                    width: "100%",
                    justifyContent: "space-between",
                  }}
                >
                  <Typography variant="subtitle1">
                    {currValues.company_name || "-"}
                  </Typography>
                </Box>
                <Typography
                  variant="subtitle1"
                  sx={{ gridColumn: 1, gridRow: 3 }}
                >
                  Line 1:
                </Typography>
                <Box
                  sx={{
                    gridColumn: 2,
                    gridRow: 3,
                    display: "flex",
                    width: "100%",
                    justifyContent: "space-between",
                  }}
                >
                  <Typography variant="subtitle1">
                    {currValues.address_line1}
                  </Typography>
                </Box>
                <Typography
                  variant="subtitle1"
                  sx={{ gridColumn: 1, gridRow: 4 }}
                >
                  Line 2:
                </Typography>
                <Box
                  sx={{
                    gridColumn: 2,
                    gridRow: 4,
                    display: "flex",
                    width: "100%",
                    justifyContent: "space-between",
                  }}
                >
                  <Typography variant="subtitle1">
                    {currValues.address_line2 || "-"}
                  </Typography>
                </Box>
                <Typography
                  variant="subtitle1"
                  sx={{ gridColumn: 1, gridRow: 5 }}
                >
                  Line 3:
                </Typography>
                <Box
                  sx={{
                    gridColumn: 2,
                    gridRow: 5,
                    display: "flex",
                    width: "100%",
                    justifyContent: "space-between",
                  }}
                >
                  <Typography variant="subtitle1">
                    {currValues.address_line3 || "-"}
                  </Typography>
                </Box>
                <Typography
                  variant="subtitle1"
                  sx={{ gridColumn: 3, gridRow: 1 }}
                >
                  City:
                </Typography>
                <Box
                  sx={{
                    gridColumn: 4,
                    gridRow: 1,
                    display: "flex",
                    width: "100%",
                    justifyContent: "space-between",
                  }}
                >
                  <Typography variant="subtitle1">
                    {currValues.city_locality}
                  </Typography>
                </Box>
                <Typography
                  variant="subtitle1"
                  sx={{ gridColumn: 3, gridRow: 2 }}
                >
                  State:
                </Typography>
                <Box
                  sx={{
                    gridColumn: 4,
                    gridRow: 2,
                    display: "flex",
                    width: "100%",
                    justifyContent: "space-between",
                  }}
                >
                  <Typography variant="subtitle1">
                    {currValues.state_province}
                  </Typography>
                </Box>
                <Typography
                  variant="subtitle1"
                  sx={{ gridColumn: 3, gridRow: 3 }}
                >
                  Country:
                </Typography>
                <Box
                  sx={{
                    gridColumn: 4,
                    gridRow: 3,
                    display: "flex",
                    width: "100%",
                    justifyContent: "space-between",
                  }}
                >
                  <Typography variant="subtitle1">
                    {currValues.country_code}
                  </Typography>
                </Box>
                <Typography
                  variant="subtitle1"
                  sx={{ gridColumn: 3, gridRow: 4 }}
                >
                  Zip:
                </Typography>
                <Box
                  sx={{
                    gridColumn: 4,
                    gridRow: 4,
                    display: "flex",
                    width: "100%",
                    justifyContent: "space-between",
                  }}
                >
                  <Typography variant="subtitle1">
                    {currValues.postal_code}
                  </Typography>
                </Box>
                <Typography
                  variant="subtitle1"
                  sx={{ gridColumn: 3, gridRow: 5 }}
                >
                  Phone:
                </Typography>
                <Box
                  sx={{
                    gridColumn: 4,
                    gridRow: 5,
                    display: "flex",
                    width: "100%",
                    justifyContent: "space-between",
                  }}
                >
                  <Typography variant="subtitle1">
                    {currValues.phone || "-"}
                  </Typography>
                </Box>
                <Typography
                  variant="subtitle1"
                  sx={{ gridColumn: 3, gridRow: 6 }}
                >
                  Email:
                </Typography>
                <Box
                  sx={{
                    gridColumn: 4,
                    gridRow: 6,
                    display: "flex",
                    width: "100%",
                    justifyContent: "space-between",
                  }}
                >
                  <Typography variant="subtitle1">
                    {currValues.email || "-"}
                  </Typography>
                </Box>
                <Typography
                  variant="subtitle1"
                  sx={{ gridColumn: 1, gridRow: 7 }}
                >
                  Residential?:
                </Typography>
                <Box
                  sx={{
                    gridColumn: 2,
                    gridRow: 7,
                    display: "flex",
                    width: "100%",
                    justifyContent: "space-between",
                  }}
                >
                  <Typography variant="subtitle1">
                    {stringUtils.capFirstLetter(
                      currValues.address_residential_indicator,
                    )}
                  </Typography>
                </Box>
              </Box>
              {editMode && (
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "repeat(2, 1fr)",
                    gridAutoRows: "auto",
                    gap: 1,
                  }}
                >
                  <TextField
                    sx={{
                      gridColumn: 1,
                      gridRow: 1,
                    }}
                    label="Name Edit"
                    value={currValues.name}
                    onChange={(e) =>
                      setValue(`${fieldName}.name`, e.target.value)
                    }
                  />
                  <TextField
                    sx={{
                      gridColumn: 1,
                      gridRow: 2,
                    }}
                    label="Company Name Edit"
                    value={currValues.company_name}
                    onChange={(e) =>
                      setValue(`${fieldName}.company_name`, e.target.value)
                    }
                  />
                  <TextField
                    sx={{
                      gridColumn: 1,
                      gridRow: 3,
                    }}
                    label="Line 1 Edit"
                    value={currValues.address_line1}
                    onChange={(e) =>
                      setValue(`${fieldName}.address_line1`, e.target.value)
                    }
                  />
                  <TextField
                    sx={{
                      gridColumn: 1,
                      gridRow: 4,
                    }}
                    label="Line 2 Edit"
                    value={currValues.address_line2}
                    onChange={(e) =>
                      setValue(`${fieldName}.address_line2`, e.target.value)
                    }
                  />
                  <TextField
                    sx={{
                      gridColumn: 1,
                      gridRow: 5,
                    }}
                    label="Line 3 Edit"
                    value={currValues.address_line3}
                    onChange={(e) =>
                      setValue(`${fieldName}.address_line3`, e.target.value)
                    }
                  />
                  <TextField
                    sx={{
                      gridColumn: 2,
                      gridRow: 1,
                    }}
                    label="City Edit"
                    value={currValues.city_locality}
                    onChange={(e) =>
                      setValue(`${fieldName}.city_locality`, e.target.value)
                    }
                  />
                  <TextField
                    sx={{
                      gridColumn: 2,
                      gridRow: 2,
                    }}
                    label="State Edit"
                    value={currValues.state_province}
                    onChange={(e) =>
                      setValue(`${fieldName}.state_province`, e.target.value)
                    }
                  />
                  <TextField
                    sx={{
                      gridColumn: 2,
                      gridRow: 3,
                    }}
                    label="Country Edit"
                    value={currValues.country_code}
                    onChange={(e) =>
                      setValue(`${fieldName}.country_code`, e.target.value)
                    }
                  />
                  <TextField
                    sx={{
                      gridColumn: 2,
                      gridRow: 4,
                    }}
                    label="Zip Edit"
                    value={currValues.postal_code}
                    onChange={(e) =>
                      setValue(`${fieldName}.postal_code`, e.target.value)
                    }
                  />
                  <TextField
                    sx={{
                      gridColumn: 2,
                      gridRow: 5,
                    }}
                    label="Phone Edit"
                    value={currValues.phone}
                    onChange={(e) =>
                      setValue(`${fieldName}.phone`, e.target.value)
                    }
                  />
                  <TextField
                    sx={{
                      gridColumn: 2,
                      gridRow: 6,
                    }}
                    label="Email Edit"
                    value={currValues.email}
                    onChange={(e) =>
                      setValue(`${fieldName}.email`, e.target.value)
                    }
                  />
                  <Autocomplete
                    sx={{
                      gridColumn: 1,
                      gridRow: 6,
                    }}
                    fullWidth
                    value={resIndicator}
                    options={ResidentialIndicatorOptions}
                    onChange={(event, value) => {
                      setValue(
                        `${fieldName}.address_residential_indicator`,
                        value
                          ? (value.value.id as ShipStationResidentialInidicator)
                          : ("unknown" as ShipStationResidentialInidicator),
                      );
                    }}
                    getOptionLabel={(option) => option?.label || ""}
                    isOptionEqualToValue={(option, value) =>
                      option.value.id === value.value.id
                    }
                    renderInput={(params) => (
                      <TextField {...params} label="Residential?" />
                    )}
                  />
                </Box>
              )}
            </Stack>
          ) : (
            <Typography variant="h6">Missing Base Address Data</Typography>
          )}
        </Popover>
      </Box>
    </Stack>
  );
};

const PackageDialog: React.FC<{
  open: boolean;
  editData: PackageFormData | null;
  onSubmit: (pkg: DialogPackageData, mode: "create" | "edit") => void;
  onCancel: () => void;
}> = ({ open, editData, onSubmit, onCancel }) => {
  const [dialogMode, setDialogMode] = useState<"create" | "edit">("create");
  const [thisPkgData, setThisPkgData] =
    useState<DialogPackageData>(dialogPkgDefaults);
  const isInit = useRef<boolean>(false);

  const initPkgData = useCallback((data: PackageFormData | null) => {
    isInit.current = true;
    if (data === null) {
      setThisPkgData(dialogPkgDefaults);
      setDialogMode("create");
    } else {
      const statOpt =
        ShipmentStatusOptions.find(
          (opt) => opt.value.id === (data.status as string),
        ) || null;
      const pkgOpt =
        PackageOptions.find((opt) => opt.value.id === data.nuc_pkg_id) || null;
      const unitOpt =
        PackageWeightUnitOptions.find(
          (opt) => opt.value.id === data.weight.unit,
        ) || null;
      setThisPkgData({
        duplicates: 1,
        status: statOpt,
        package: pkgOpt,
        dimensions: data.dimensions,
        weight: { value: data.weight.value.toString(), unit: unitOpt },
        tracking: data.tracking,
        package_contents: data.package_contents,
        is_new: data.is_new,
        movements: data.movements,
        description: data.description,
      });
      setDialogMode("edit");
    }
    isInit.current = false;
  }, []);

  const handlePkgTypeChange = (event: any, value: PackageOption | null) => {
    let weight = dialogPkgDefaults.weight;
    if (value?.value.weight) {
      weight = {
        value: value.value.weight.value.toString(),
        unit:
          PackageWeightUnitOptions.find(
            (opt) => opt.value.id === value?.value.weight?.unit,
          ) || PackageWeightUnitOptions[0],
      };
    }
    setThisPkgData((prev) => ({
      ...prev,
      package: value,
      dimensions: value?.value.dimensions || dialogPkgDefaults.dimensions,
      weight: weight,
    }));
  };

  const handleDialogSubmit = (event: any, reason: string | null) => {
    if (reason === "backdropClick") {
      return;
    }
    onSubmit(thisPkgData, dialogMode);
  };
  const handleDialogCancel = () => {
    onCancel();
  };

  useEffect(() => {
    console.log("DIALOG OPEN", open);
    if (open) {
      initPkgData(editData);
    }
  }, [open, initPkgData, editData]);

  return (
    <Dialog open={open} maxWidth="md" fullWidth>
      <DialogTitle>
        {stringUtils.capFirstLetter(dialogMode) + " Package"}
      </DialogTitle>
      <DialogContent>
        <Stack direction="column" gap={2}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gridAutoRows: "auto",
              p: 5,
              rowGap: 1,
              columnGap: 1,
            }}
          >
            {dialogMode === "create" && (
              <NumericEntryField
                value={thisPkgData.duplicates}
                onChange={(value) =>
                  setThisPkgData((prev) => ({
                    ...prev,
                    duplicates: value,
                  }))
                }
                min={0}
                decimalPlaces={0}
                isCurrency={false}
                label={"# Of This Package To Create"}
                textFieldProps={{
                  sx: {
                    gridColumn: 1,
                    gridRow: 1,
                  },
                  fullWidth: true,
                  size: "small",
                }}
              />
            )}
            <Autocomplete
              sx={{ gridColumn: 2, gridRow: 1 }}
              fullWidth
              value={thisPkgData.status}
              options={ShipmentStatusOptions}
              onChange={(e, v) =>
                setThisPkgData((prev) => ({
                  ...prev,
                  status: v,
                }))
              }
              getOptionLabel={(option) => option?.label || ""}
              isOptionEqualToValue={(option, value) =>
                option.value.id === value.value.id
              }
              renderInput={(params) => (
                <TextField {...params} size="small" label="Package Status" />
              )}
            />
            <Autocomplete
              sx={{ gridColumnStart: 1, gridColumnEnd: 3, gridRow: 2 }}
              fullWidth
              value={thisPkgData.package}
              options={PackageOptions}
              onChange={handlePkgTypeChange}
              getOptionLabel={(option) => option?.label || ""}
              isOptionEqualToValue={(option, value) =>
                option.value.id === value.value.id
              }
              renderInput={(params) => (
                <TextField {...params} size="small" label="Package Type" />
              )}
            />
            <Box
              sx={{
                display: "flex",
                gap: 2,
                justifyContent: "space-evenly",
                gridColumnStart: 1,
                gridColumnEnd: 3,
                gridRow: 3,
              }}
            >
              <NumericEntryField
                value={Number(thisPkgData.dimensions.height || "0")}
                onChange={(value) => {
                  setThisPkgData((prev) => ({
                    ...prev,
                    dimensions: {
                      length: prev.dimensions.length,
                      height: value,
                      width: prev.dimensions.width,
                    },
                  }));
                }}
                min={0}
                decimalPlaces={2}
                isCurrency={false}
                label={"Pkg Height (in)"}
                textFieldProps={{
                  fullWidth: true,
                  size: "small",
                }}
              />
              <NumericEntryField
                value={Number(thisPkgData.dimensions.length || "0")}
                onChange={(value) => {
                  setThisPkgData((prev) => ({
                    ...prev,
                    dimensions: {
                      length: value,
                      height: prev.dimensions.height,
                      width: prev.dimensions.width,
                    },
                  }));
                }}
                min={0}
                decimalPlaces={2}
                isCurrency={false}
                label={"Pkg Length (in)"}
                textFieldProps={{
                  fullWidth: true,
                  size: "small",
                }}
              />
              <NumericEntryField
                value={Number(thisPkgData.dimensions.width || "0")}
                onChange={(value) => {
                  setThisPkgData((prev) => ({
                    ...prev,
                    dimensions: {
                      length: prev.dimensions.length,
                      height: prev.dimensions.height,
                      width: value,
                    },
                  }));
                }}
                min={0}
                decimalPlaces={2}
                isCurrency={false}
                label={"Pkg Width (in)"}
                textFieldProps={{
                  fullWidth: true,
                  size: "small",
                }}
              />
            </Box>
            <NumericEntryField
              value={Number(thisPkgData.weight.value || "0")}
              onChange={(value) => {
                setThisPkgData((prev) => ({
                  ...prev,
                  weight: {
                    value: value.toString(),
                    unit: prev.weight.unit,
                  },
                }));
              }}
              min={0}
              decimalPlaces={2}
              isCurrency={false}
              label={"Pkg Weight"}
              textFieldProps={{
                sx: {
                  gridColumn: 1,
                  gridRow: 4,
                },
                fullWidth: true,
                size: "small",
              }}
            />
            <Autocomplete
              sx={{ gridColumn: 2, gridRow: 4 }}
              fullWidth
              value={thisPkgData.weight.unit}
              options={PackageWeightUnitOptions}
              onChange={(e, v) => {
                setThisPkgData((prev) => ({
                  ...prev,
                  weight: {
                    value: prev.weight.value,
                    unit: v,
                  },
                }));
              }}
              getOptionLabel={(option) => option?.label || ""}
              isOptionEqualToValue={(option, value) =>
                option.value.id === value.value.id
              }
              renderInput={(params) => (
                <TextField {...params} size="small" label="Weight Unit" />
              )}
            />
            <TextField
              sx={{
                gridColumnStart: 1,
                gridColumnEnd: 3,
                gridRow: 5,
              }}
              size="small"
              fullWidth
              multiline
              rows={3}
              value={thisPkgData.package_contents || ""}
              onChange={(e) =>
                setThisPkgData((prev) => ({
                  ...prev,
                  package_contents: e.target.value,
                }))
              }
              label="Package Contents"
            />
            <TextField
              sx={{
                gridColumnStart: 1,
                gridColumnEnd: 3,
                gridRow: 6,
              }}
              size="small"
              fullWidth
              multiline
              rows={3}
              value={thisPkgData.description || ""}
              onChange={(e) =>
                setThisPkgData((prev) => ({
                  ...prev,
                  description: e.target.value,
                }))
              }
              label="Additional Package Notes"
            />
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" color="error" onClick={handleDialogCancel}>
          {dialogMode === "edit"
            ? "Cancel Package Edits"
            : "Cancel New Package"}
        </Button>
        <Button
          variant="contained"
          color="success"
          onClick={(e) => handleDialogSubmit(e, "saved")}
        >
          {dialogMode === "edit" ? "Save Package Edits" : "Save New Package"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const PageShipmentForm: React.FC = () => {
  const urlLocation = useLocation();

  const searchParams = new URLSearchParams(urlLocation.search);
  const id = searchParams.get("id");
  const formMode = searchParams.get("mode");
  const refFormMode = useRef(formMode);

  const worldLocContext = useWorldLocationData();

  const [recId, setRecId] = useState<string | null>(id);

  const today = new Date();
  const tomorrow = (() => {
    let temp = new Date(today);
    temp.setDate(temp.getDate() + 1);
    return temp;
  })();

  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
    watch,
    setValue,
    getValues,
  } = useForm<ShipmentFormData>({
    resolver: yupResolver(shipYupSchema) as any,
    defaultValues: formDefaults,
  });

  const refRenderCount = useRef<number>(0);
  const refOrigData = useRef<ShipmentRecordFull | null>(null);
  const [navToView, setNavToView] = useState<boolean>(false);
  const [displayProcessDialog, setDisplayProcessDialog] =
    useState<boolean>(false);

  const handleProcessDialogClose = useCallback(
    (event: any, reason: "backdropClick" | "escapeKeyDown") => {
      if (reason === "backdropClick") {
        return;
      }
      setDisplayProcessDialog(false);
      if (navToView && recId) {
        window.open(`/shipments/form?mode=view&id=${recId}`, "_self");
      }
    },
    [navToView, recId],
  );

  const [error, setError] = useState<string | null>(null);
  const [createShipStationOrder, setCreateShipStationOrder] =
    useState<boolean>(false);

  const [formResetting, setFormResetting] = useState<boolean>(false);

  const createShipmentMutation = useTSMCreateShipment();
  const updateShipmentMutation = useTSMUpdateShipment();

  const {
    data: thisShipmentData,
    isLoading: thisShipmentLoading,
    error: thisShipmentError,
  } = useTSQShipment(
    recId || "",
    (formMode === "view" || formMode === "edit") && !formResetting,
  );

  const {
    data: itemsResponse,
    isLoading: itemsLoading,
    error: itemsError,
  } = useTSQItemList({
    active_status: "active",
    filters: [
      [
        { field: "item_type", operator: "not_in", value: ["SERVICE"] },
        { field: "number", operator: "nlike", value: "LBL-%" },
      ],
    ],
  });
  const itemOpts: ItemOption[] = useMemo(() => {
    if (itemsResponse?.items) {
      const opts: ItemOption[] = itemsResponse.items.map((val) => ({
        label: val.number,
        type: "item",
        group: val.item_type,
        value: {
          id: val.id,
          number: val.number,
          name: val.name,
          uomTypeId: val.uom_type.id,
          preferredUom: null,
          item_type: val.item_type,
          expires: val.item_expires,
        },
      }));
      const sorted = opts.sort((a, b) => {
        if (a.group === b.group) {
          return a.label.localeCompare(b.label);
        } else {
          return (a.group || "NOGROUP").localeCompare(b.group || "NOGROUP");
        }
      }) as ItemOption[];
      sorted.unshift(CustomItemOption);
      return sorted;
    } else {
      return [];
    }
  }, [itemsResponse]);

  const {
    data: unitsResponse,
    isLoading: unitsLoading,
    error: unitsError,
  } = useTSQUOMUnitList({
    limit: -1,
    active_status: "active",
    filters: [[]],
  });
  const uomOpts: UOMOption[] = useMemo(() => {
    if (unitsResponse?.items) {
      const opts: UOMOption[] = unitsResponse.items.map((val) => ({
        label: val.unit_symbol,
        type: "unit",
        value: {
          id: val.id,
          symbol: val.unit_symbol,
          conversion: parseFloat(val.conversion_factor),
          typeId: val.uom_type_id,
        },
      }));
      opts.unshift(CustomUOMOption);
      return opts;
    } else {
      return [];
    }
  }, [unitsResponse]);

  const {
    data: custResponse,
    isLoading: custLoading,
    error: custError,
  } = useTSQCompanyList(
    {
      active_status: "active",
      filters: [[]],
    },
    "customer",
    true,
  );
  const customerOpts: EntityOption[] = useMemo(() => {
    if (custResponse?.items) {
      return custResponse.items.map((val) => ({
        label: `${val.code} - ${val.name}`,
        group: "Customers",
        type: "entity",
        value: {
          id: val.id,
          entity_type: "customer",
        },
      }));
    } else {
      return [];
    }
  }, [custResponse]);

  const {
    data: vendResponse,
    isLoading: vendLoading,
    error: vendError,
  } = useTSQCompanyList(
    {
      active_status: "active",
      filters: [[]],
    },
    "vendor",
    true,
  );
  const vendorOpts: EntityOption[] = useMemo(() => {
    if (vendResponse?.items) {
      return vendResponse.items.map((val) => ({
        label: `${val.code} - ${val.name}`,
        group: "Vendors",
        type: "entity",
        value: {
          id: val.id,
          entity_type: "vendor",
        },
      }));
    } else {
      return [];
    }
  }, [vendResponse]);

  const {
    data: empsResponse,
    isLoading: empsLoading,
    error: empsError,
  } = useTSQPeopleList(
    {
      limit: -1,
      skip: -1,
      active_status: "active",
      filters: [[]],
    },
    "employee",
  );
  const empOpts: EntityOption[] = useMemo(() => {
    if (empsResponse?.items) {
      return empsResponse.items.map((val) => ({
        label: `${val.first_name} ${val.last_name}`,
        group: "Employees",
        type: "entity",
        value: { id: val.id, entity_type: "employee" },
      }));
    } else {
      return [];
    }
  }, [empsResponse]);

  const entityOpts: EntityOption[] = useMemo(() => {
    return [CenteseEntityOption, CustomEntityOption].concat(
      customerOpts,
      vendorOpts,
      empOpts,
    );
  }, [customerOpts, vendorOpts, empOpts]);

  const baseDataLoading =
    itemsLoading || unitsLoading || custLoading || vendLoading || empsLoading;

  const baseDataReady =
    !baseDataLoading &&
    itemOpts.length > 0 &&
    entityOpts.length > 0 &&
    uomOpts.length > 0;

  const [formInitState, setFormInitState] = useState<boolean>(false);

  const [pkgDialogOpen, setPkgDialogOpen] = useState<boolean>(false);
  const [pkgEditData, setPkgEditData] = useState<PackageFormData | null>(null);

  const [shipToEntity, setShipToEntity] = useState<EntityOption | null>(null);
  const [shipToAddress, setShipToAddress] = useState<AddressOption | null>(
    null,
  );

  const {
    data: toAddrResponse,
    isLoading: toAddrLoading,
    error: toAddrError,
  } = useTSQAddressList(
    {
      skip: -1,
      limit: -1,
      active_status: "active",
      filters: [
        [
          {
            field: "entity_id",
            operator: "==",
            value: shipToEntity?.value.id,
          },
        ],
      ],
    },
    !!shipToEntity,
  );
  const toAddrOpts: AddressOption[] = useMemo(() => {
    let opts: AddressOption[] = [];
    if (toAddrResponse?.items) {
      opts = toAddrResponse.items.map((val) => ({
        label: val.name,
        type: "address",
        value: { id: val.id, entity_id: val.entity_id },
      }));
    } else if (shipToEntity?.value.id === "centese") {
      opts = [CenteseAddressOption];
    }
    opts.unshift(CustomAddressOption);
    return opts;
  }, [toAddrResponse, shipToEntity]);

  const [shipFromEntity, setShipFromEntity] = useState<EntityOption | null>(
    null,
  );
  const [shipFromAddress, setShipFromAddress] = useState<AddressOption | null>(
    null,
  );

  const {
    data: fromAddrResponse,
    isLoading: fromAddrLoading,
    error: fromAddrError,
  } = useTSQAddressList(
    {
      skip: -1,
      limit: -1,
      active_status: "active",
      filters: [
        [
          {
            field: "entity_id",
            operator: "==",
            value: shipFromEntity?.value.id,
          },
        ],
      ],
    },
    !!shipFromEntity,
  );
  const fromAddrOpts: AddressOption[] = useMemo(() => {
    let opts: AddressOption[] = [];
    if (fromAddrResponse?.items) {
      opts = fromAddrResponse.items.map((val) => ({
        label: val.name,
        type: "address",
        value: { id: val.id, entity_id: val.entity_id },
      }));
    } else if (shipFromEntity?.value.id === "centese") {
      opts = [CenteseAddressOption];
    }
    opts.unshift(CustomAddressOption);
    return opts;
  }, [fromAddrResponse, shipFromEntity]);

  const initFormWithData = useCallback(
    (shipmentData: ShipmentRecordFull | null) => {
      refOrigData.current = shipmentData;
      if (shipmentData) {
        const statOpt =
          ShipmentStatusOptions.find(
            (opt) => opt.value.id === shipmentData.status,
          ) || null;
        const reasonOpt =
          ShipmentReasonOptions.find(
            (opt) => opt.value.id === shipmentData.reason,
          ) || null;
        const carrierOpt =
          ShippingCarrierOptions.find(
            (opt) => opt.value.id === shipmentData.carrier,
          ) || null;
        const methodOpt = ShippingMethodOptions[shipmentData.carrier].find(
          (opt) => opt.value.id === shipmentData.method,
        );
        const shipToEOpt =
          entityOpts.find(
            (opt) => opt.value.id === shipmentData.ship_to.nuc_entity_id,
          ) || null;
        setShipToEntity(shipToEOpt);
        const shipFromEOpt =
          entityOpts.find(
            (opt) => opt.value.id === shipmentData.ship_from.nuc_entity_id,
          ) || null;
        setShipFromEntity(shipFromEOpt);
        const assocRecTypeOpt = null;
        const assocRecOpt = null;
        const packages: PackageFormData[] = shipmentData.packages.map(
          (pkg) => ({
            status: pkg.status,
            nuc_pkg_id: pkg.nuc_pkg_id,
            dimensions: pkg.dimensions,
            weight: pkg.weight,
            tracking: pkg.tracking,
            package_number: pkg.package_number,
            package_contents: pkg.package_contents,
            is_new: false,
            movements: [],
            description: pkg.description || null,
            created_at: pkg.created_at,
            updated_at: pkg.updated_at,
          }),
        );
        reset({
          status: statOpt,
          reason: reasonOpt,
          reason_other: shipmentData.reason_other,
          carrier: carrierOpt,
          carrier_other: shipmentData.carrier_other,
          method: methodOpt,
          associated_record_type: assocRecTypeOpt,
          associated_record: assocRecOpt,
          ship_to: shipmentData.ship_to,
          ship_from: shipmentData.ship_from,
          packages: packages,
          primary_tracking: shipmentData.primary_tracking,
          shipment_date: shipmentData.shipment_date,
          description: shipmentData.description || null,
          created_at: shipmentData.created_at,
          updated_at: shipmentData.updated_at,
        });
      } else {
        reset(formDefaults);
      }
    },
    [entityOpts, reset],
  );

  const {
    fields: shipmentPackages,
    append: appendPackage,
    remove: removePackage,
    update: updatePackage,
  } = useFieldArray({
    control,
    name: "packages",
  });

  const [watchedReason, watchedCarrier, shipToVals, shipFromVals] = watch([
    "reason",
    "carrier",
    "ship_to",
    "ship_from",
  ]);

  useEffect(() => {
    refRenderCount.current += 1;
  });

  // Mode switching functions
  const switchToViewMode = () => {
    window.open(`/shipments/form?mode=view&id=${recId}`, "_self");
  };

  const switchToEditMode = () => {
    window.open(`/shipments/form?mode=edit&id=${recId}`, "_self");
  };

  const switchToCreateMode = () => {
    window.open(`/shipments/form?mode=create`, "_self");
  };

  const handlePkgDialogOpen = (pkg: PackageFormData | null) => {
    setPkgEditData(pkg);
    setPkgDialogOpen(true);
  };

  const handlePkgDialogSubmit = (
    pkg: DialogPackageData,
    mode: "create" | "edit",
  ) => {};

  const handlePkgDialogCancel = () => {
    setPkgDialogOpen(false);
    setPkgEditData(null);
  };

  const onSubmit = async (data: ShipmentFormData) => {};

  const handleCancel = () => {
    window.open("/shipments", "_self");
  };
  const getShipmentStatusColor = (
    status: ShipmentStatus | undefined,
  ):
    | "error"
    | "success"
    | "info"
    | "warning"
    | "primary"
    | "secondary"
    | "default" => {
    switch (status) {
      case "STAGING":
        return "default";
      case "HOLDING":
        return "warning";
      case "LABEL_CREATED":
        return "secondary";
      case "IN_TRANSIT":
        return "info";
      case "DELIVERED":
        return "primary";
      case "ERROR":
        return "error";
      case "VOID":
        return "warning";
      default:
        return "default";
    }
  };
  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Stack direction="column" sx={{ p: 3 }}>
        {/* Header */}
        <Typography variant="h4" component="h1">
          {formMode === "create"
            ? "Create Shipment"
            : formMode === "view"
              ? `View Shipment ${thisShipmentData?.name}`
              : `Edit Shipment ${thisShipmentData?.name}`}
        </Typography>
        <Box display="flex" alignItems="center" gap={2} sx={{ mb: 3 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={handleCancel}
            variant="outlined"
          >
            To Shipment List
          </Button>
          {formMode === "view" && (
            <Button
              variant="outlined"
              onClick={switchToCreateMode}
              startIcon={<AddIcon />}
            >
              Create New
            </Button>
          )}
          {formMode === "view" && (
            <Button
              variant="outlined"
              onClick={switchToEditMode}
              startIcon={<EditIcon />}
            >
              Edit
            </Button>
          )}
          {formMode === "edit" && (
            <Button
              variant="outlined"
              onClick={switchToViewMode}
              startIcon={<ViewIcon />}
            >
              View
            </Button>
          )}
          <Button
            variant="outlined"
            onClick={() => {
              console.log(getValues());
            }}
          >
            DEBUG PRINT
          </Button>
        </Box>

        {/* Error Alert */}
        {(error ||
          thisShipmentError ||
          itemsError ||
          unitsError ||
          custError ||
          vendError ||
          empsError ||
          toAddrError ||
          fromAddrError) && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error ||
              thisShipmentError?.message ||
              itemsError?.message ||
              unitsError?.message ||
              custError?.message ||
              vendError?.message ||
              empsError?.message ||
              toAddrError?.message ||
              fromAddrError?.message ||
              "An unknown error occurred"}
          </Alert>
        )}

        {/* Form */}
        <Paper sx={{ p: 3 }}>
          <form onSubmit={handleSubmit(onSubmit, console.error)}>
            <Stack spacing={3} sx={{ width: "100%" }}>
              {formMode !== "create" && (
                <Box sx={{ display: "flex", gap: 2 }}>
                  <Typography variant="h6">Shipment Status:</Typography>
                  <Chip
                    label={stringUtils.capFirstLetter(
                      (thisShipmentData?.status || "").replace(/_/g, " "),
                    )}
                    color={getShipmentStatusColor(thisShipmentData?.status)}
                    size="small"
                    clickable={false}
                    onClick={() => {}}
                  />
                </Box>
              )}
              <Typography variant="h6" gutterBottom>
                Basic Information
              </Typography>
              <Box sx={{ display: "flex", gap: 2, width: "100%" }}>
                <Stack sx={{ direction: "column", gap: 2, width: "50%" }}>
                  <Controller
                    name="name"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Shipment Name"
                        fullWidth
                        size="small"
                        disabled={formMode === "view"}
                        error={!!errors.name}
                        helperText={errors.name?.message}
                        slotProps={{
                          inputLabel: { shrink: true },
                        }}
                      />
                    )}
                  />
                  <Controller
                    name="status"
                    control={control}
                    render={({ field: { value, onChange, ...restField } }) => (
                      <Autocomplete
                        {...restField}
                        fullWidth
                        value={value}
                        options={ShipmentStatusOptions}
                        onChange={(event, newValue) => {
                          onChange(newValue);
                        }}
                        disabled={formMode !== "create" && formMode !== "edit"}
                        getOptionLabel={(option) => option?.label || ""}
                        isOptionEqualToValue={(option, value) =>
                          option.value.id === value?.value.id
                        }
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            size="small"
                            label="Shipment Status"
                          />
                        )}
                      />
                    )}
                  />
                  <Controller
                    name="reason"
                    control={control}
                    render={({ field: { value, onChange, ...restField } }) => (
                      <Autocomplete
                        {...restField}
                        fullWidth
                        value={value}
                        options={ShipmentReasonOptions}
                        onChange={(event, newValue) => {
                          onChange(newValue);
                        }}
                        disabled={formMode !== "create" && formMode !== "edit"}
                        getOptionLabel={(option) => option?.label || ""}
                        isOptionEqualToValue={(option, value) =>
                          option.value.id === value?.value.id
                        }
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            size="small"
                            label="Shipment Reason"
                          />
                        )}
                      />
                    )}
                  />
                  <Controller
                    name="reason_other"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        size="small"
                        fullWidth
                        label="Reason Other"
                        disabled={
                          (formMode !== "create" && formMode !== "edit") ||
                          watchedReason?.value.id !== "OTHER"
                        }
                      />
                    )}
                  />
                </Stack>
                <Stack sx={{ direction: "column", gap: 2, width: "50%" }}>
                  <Controller
                    name="shipment_date"
                    control={control}
                    render={({ field }) => (
                      <DatePicker
                        label="Shipment Date"
                        value={field.value ? new Date(field.value) : null}
                        onChange={(date) => field.onChange(date?.toISOString())}
                        disabled={formMode === "view"}
                        slotProps={{
                          textField: {
                            size: "small",
                            error: !!errors.shipment_date,
                            helperText: errors.shipment_date?.message,
                          },
                        }}
                      />
                    )}
                  />
                  <Controller
                    name="carrier"
                    control={control}
                    render={({ field: { value, onChange, ...restField } }) => (
                      <Autocomplete
                        {...restField}
                        fullWidth
                        value={value}
                        options={ShippingCarrierOptions}
                        onChange={(event, newValue) => {
                          onChange(newValue);
                          if (newValue) {
                            setValue(
                              "method",
                              ShippingMethodOptions[
                                newValue.value.id as ShippingCarrier
                              ][0],
                            );
                          }
                        }}
                        disabled={formMode !== "create" && formMode !== "edit"}
                        getOptionLabel={(option) => option?.label || ""}
                        isOptionEqualToValue={(option, value) =>
                          option.value.id === value?.value.id
                        }
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            size="small"
                            label="Shipping Carrier"
                          />
                        )}
                      />
                    )}
                  />
                  <Controller
                    name="carrier_other"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        size="small"
                        fullWidth
                        label="Carrier Other"
                        disabled={
                          (formMode !== "create" && formMode !== "edit") ||
                          watchedCarrier?.value.id !== "OTHER"
                        }
                      />
                    )}
                  />
                  <Controller
                    name="method"
                    control={control}
                    render={({ field: { value, onChange, ...restField } }) => (
                      <Autocomplete
                        {...restField}
                        fullWidth
                        value={value}
                        options={
                          ShippingMethodOptions[
                            (watchedCarrier?.value.id ||
                              "NONE") as ShippingCarrier
                          ]
                        }
                        onChange={(event, newValue) => {
                          onChange(newValue);
                        }}
                        disabled={formMode !== "create" && formMode !== "edit"}
                        getOptionLabel={(option) => option?.label || ""}
                        isOptionEqualToValue={(option, value) =>
                          option.value.id === value?.value.id
                        }
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            size="small"
                            label="Ship Method"
                          />
                        )}
                      />
                    )}
                  />
                </Stack>
              </Box>
              <Box sx={{ display: "flex", gap: 2, width: "100%" }}>
                <Stack sx={{ direction: "column", gap: 2, width: "50%" }}>
                  <Typography variant="h6" gutterBottom>
                    From
                  </Typography>
                  <Autocomplete
                    fullWidth
                    value={shipFromEntity}
                    options={entityOpts}
                    onChange={(event, newValue) => {
                      setShipFromEntity(newValue);
                      setShipFromAddress(null);
                      setValue("ship_from", DefaultShipDestination);
                    }}
                    disabled={formMode !== "create" && formMode !== "edit"}
                    getOptionLabel={(option) => option?.label || ""}
                    isOptionEqualToValue={(option, value) =>
                      option.value.id === value?.value.id
                    }
                    groupBy={(option) => option.group || "NOGROUP"}
                    renderInput={(params) => (
                      <TextField {...params} size="small" label="Ship From" />
                    )}
                  />
                  <ShipAddressEditField
                    formMode={formMode || ""}
                    selectLabel="From Address"
                    fieldName="ship_from"
                    currOpt={shipFromAddress}
                    updateCurrOpt={setShipFromAddress}
                    availOpts={fromAddrOpts}
                    setValue={setValue}
                    currValues={shipFromVals}
                    errors={errors.ship_from}
                    textFieldProps={{ size: "small", fullWidth: true }}
                  />
                </Stack>
                <Stack sx={{ direction: "column", gap: 2, width: "50%" }}>
                  <Typography variant="h6" gutterBottom>
                    To
                  </Typography>
                  <Autocomplete
                    fullWidth
                    value={shipToEntity}
                    options={entityOpts}
                    onChange={(event, newValue) => {
                      setShipToEntity(newValue);
                      setShipToAddress(null);
                      setValue("ship_to", DefaultShipDestination);
                    }}
                    disabled={formMode !== "create" && formMode !== "edit"}
                    getOptionLabel={(option) => option?.label || ""}
                    isOptionEqualToValue={(option, value) =>
                      option.value.id === value?.value.id
                    }
                    renderInput={(params) => (
                      <TextField {...params} size="small" label="Ship To" />
                    )}
                  />
                  <ShipAddressEditField
                    formMode={formMode || ""}
                    selectLabel="To Address"
                    fieldName="ship_to"
                    currOpt={shipToAddress}
                    updateCurrOpt={setShipToAddress}
                    availOpts={toAddrOpts}
                    setValue={setValue}
                    currValues={shipToVals}
                    errors={errors.ship_to}
                    textFieldProps={{ size: "small", fullWidth: true }}
                  />
                </Stack>
              </Box>

              <Divider />
              {!(formMode === "view") && (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 2,
                  }}
                >
                  <Button
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={() => handlePkgDialogOpen(null)}
                  >
                    Add Package
                  </Button>
                </Box>
              )}

              {/* Form Actions */}
              {!(formMode === "view") && (
                <Box
                  sx={{
                    display: "flex",
                    gap: 2,
                    justifyContent: "flex-end",
                    pt: 2,
                  }}
                >
                  <Button
                    variant="outlined"
                    onClick={handleCancel}
                    disabled={isSubmitting}
                    startIcon={<CancelIcon />}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    startIcon={<SaveIcon />}
                    disabled={isSubmitting}
                  >
                    {isSubmitting
                      ? "Saving..."
                      : formMode === "create"
                        ? "Create Purchase Order"
                        : formMode === "receive"
                          ? "Receive Purchase Order"
                          : "Update Purchase Order"}
                  </Button>
                </Box>
              )}
              <PackageDialog
                open={pkgDialogOpen}
                editData={pkgEditData}
                onSubmit={handlePkgDialogSubmit}
                onCancel={handlePkgDialogCancel}
              />
              <Dialog
                open={displayProcessDialog}
                onClose={(e, r) => {
                  if (formMode !== "receive") {
                    return;
                  } else {
                    handleProcessDialogClose(e, r);
                  }
                }}
                disableEscapeKeyDown
                maxWidth="sm"
                fullWidth
              >
                <DialogContent>
                  {formMode === "create" ? (
                    <Stack direction="column" gap={3}>
                      <Box display="flex" gap={2} justifyContent="space-evenly">
                        {/* <Typography variant="h5">Order</Typography>
                        {createPOMutation.isPending ? (
                          <CircularProgress />
                        ) : createPOMutation.isSuccess ? (
                          <CheckIcon color="primary" />
                        ) : (
                          <CancelIcon color="error" />
                        )} */}
                      </Box>
                      {/* {createPOMutation.isPending ? (
                        <Typography variant="h5">
                          Form will navigate to "View" mode once requests are
                          completed...
                        </Typography>
                      ) : (
                        <Typography variant="h5">
                          Requests completed, returning to "View" mode...
                        </Typography>
                      )} */}
                    </Stack>
                  ) : formMode === "receive" ? (
                    <Stack direction="column" gap={3}>
                      <Box display="flex" gap={2} justifyContent="space-evenly">
                        <Typography variant="h5">Receiving Lots...</Typography>
                        {/* {receivePOMutation.isPending ? (
                          <CircularProgress />
                        ) : receivePOMutation.isSuccess ? (
                          <CheckIcon color="primary" />
                        ) : (
                          <CancelIcon color="error" />
                        )} */}
                      </Box>
                      {/* {receivePOMutation.isError ? (
                        <Typography variant="h5" color="error">
                          ERROR RECEIVING PO
                        </Typography>
                      ) : !formsToGen ? (
                        <Typography variant="h5">
                          Waiting for lots to be received...
                        </Typography>
                      ) : !!formsToGen ? (
                        <Stack direction="column" gap={1}>
                          {Object.entries(formsToGen).map((val, index) => {
                            return (
                              <Box
                                display="flex"
                                gap={2}
                                justifyContent="space-evenly"
                              >
                                <Typography variant="body1">
                                  {val[0]}
                                </Typography>
                                {val[1].link === null ? (
                                  <CircularProgress />
                                ) : val[1].status ? (
                                  <IconButton
                                    onClick={() => {
                                      window.open(
                                        val[1].link || undefined,
                                        "_blank",
                                        "noopener,noreferrer"
                                      );
                                    }}
                                  >
                                    <FileIcon color="primary" />
                                  </IconButton>
                                ) : (
                                  <CancelIcon color="error" />
                                )}
                              </Box>
                            );
                          })}
                        </Stack>
                      ) : (
                        <></>
                      )} */}
                      {/* {!receivePOMutation.isPending && allFormsGen && (
                        <Button
                          variant="contained"
                          onClick={() => {
                            handleProcessDialogClose(null, "escapeKeyDown");
                          }}
                        >
                          {`Close And Return to ${
                            navToView ? "View" : "Receive"
                          } Mode`}
                        </Button>
                      )} */}
                    </Stack>
                  ) : (
                    <Stack direction="column" gap={3}>
                      <Box display="flex" gap={2} justifyContent="space-evenly">
                        <Typography variant="h5">Updating Order</Typography>
                        {/* {updatePOMutation.isPending ? (
                          <CircularProgress />
                        ) : updatePOMutation.isSuccess ? (
                          <CheckIcon color="primary" />
                        ) : (
                          <CancelIcon color="error" />
                        )} */}
                      </Box>
                      {/* {updatePOMutation.isPending ? (
                        <Typography variant="h5">
                          Form will navigate to "View" mode once requests are
                          completed...
                        </Typography>
                      ) : (
                        <Typography variant="h5">
                          Requests completed, returning to "View" mode...
                        </Typography>
                      )} */}
                    </Stack>
                  )}
                </DialogContent>
              </Dialog>
            </Stack>
          </form>
        </Paper>
      </Stack>
    </LocalizationProvider>
  );
};

export default PageShipmentForm;
